/**
 * SKY FX — small ambient touches on the wobble clock: four-point sparkles that pop on the sea
 * (thick in the sun/moon glare, a few across the open water) and, at night, now and then a
 * shooting star with a tapered tail.
 *
 * Pure layouts: both renderers draw the same list (Canvas 2D below, the Pixi sky layer as
 * sprites). Everything comes from hashes of the clock's time window, never Math.random, so a
 * held clock gives a still frame and the static-sky snapshot stays valid. Window 0 never holds
 * a shooting star, so a static sky (clock held at 0) never freezes one mid-flight.
 */

import { getStarCanvas, getStreakCanvas, STAR_SPRITE_FILL, STREAK_BAND } from "../toon/sky-art.js";

export var SEA_SPARKLE_MAX = 10;
var GLARE_SLOTS = 6;
var OPEN_SLOTS = 4;
/** Seconds between shooting-star chances, and how long one flies. */
export var SHOOTING_STAR_PERIOD = 7;
var SHOOTING_STAR_FLIGHT = 0.95;
/** Colour of the shooting star's sparkle head. */
export var SHOOTING_STAR_HEAD_RGB = [255, 240, 170];

function hash(n) {
  var s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * Sea sparkles for one frame.
 * @param {{ width: number, seaLineY: number, seaH: number, scaleUi: number, wobbleSec: number,
 *   glareX?: number, glareStrength?: number, glareHalf?: number }} o
 * @returns {{ x: number, y: number, r: number, alpha: number }[]}
 */
export function layoutSeaSparkles(o) {
  var out = [];
  var seaH = Number(o.seaH);
  var w = Number(o.width);
  if (!(seaH > 8) || !(w > 0)) return out;
  var s = Math.max(0.05, Number(o.scaleUi) || 1);
  var t = Number(o.wobbleSec) || 0;
  var glare = Math.max(0, Math.min(1, Number(o.glareStrength) || 0));
  var slots = (glare > 0.05 ? GLARE_SLOTS : 0) + OPEN_SLOTS;
  for (var i = 0; i < slots; i++) {
    var inGlare = i < GLARE_SLOTS && glare > 0.05;
    var period = 1.5 + hash(i * 7.3 + 0.5) * 1.3;
    var shifted = t + hash(i * 3.1 + 1.7) * period;
    var cycle = Math.floor(shifted / period);
    var k = (shifted - cycle * period) / period;
    // Pop in and out over the first half of each cycle, rest dark for the second half.
    if (k > 0.5) continue;
    var pop = Math.sin((k / 0.5) * Math.PI);
    var seed = i * 13.7 + cycle * 3.97;
    var d = hash(seed + 0.3);
    var x;
    var y;
    var r;
    var alpha;
    if (inGlare) {
      var half = (Number(o.glareHalf) || 60 * s) * (0.3 + 0.9 * d);
      x = Number(o.glareX) + (hash(seed + 1.1) - 0.5) * 2 * half;
      y = o.seaLineY + seaH * (0.04 + 0.8 * Math.pow(d, 1.3));
      r = (4.5 + 5.5 * d) * s * pop;
      alpha = glare;
    } else {
      x = hash(seed + 2.9) * w;
      y = o.seaLineY + seaH * (0.08 + 0.85 * d);
      r = (3 + 3.5 * d) * s * pop;
      alpha = 0.75;
    }
    if (r < 0.6 || out.length >= SEA_SPARKLE_MAX) continue;
    out.push({ x: x, y: y, r: r, alpha: alpha });
  }
  return out;
}

/**
 * The shooting star in flight at this moment of the wobble clock, or null.
 * @param {{ width: number, skyH: number, scaleUi: number, wobbleSec: number, starAlpha: number }} o
 * @returns {{ x: number, y: number, angle: number, tail: number, r: number, alpha: number }|null}
 */
export function layoutShootingStar(o) {
  if (!(o.starAlpha > 0.5) || !(o.skyH > 40) || !(o.width > 0)) return null;
  var t = Number(o.wobbleSec) || 0;
  var win = Math.floor(t / SHOOTING_STAR_PERIOD);
  if (win < 1 || hash(win * 5.31 + 0.7) < 0.35) return null;
  var local = t - win * SHOOTING_STAR_PERIOD - hash(win * 2.17) * 3;
  if (local < 0 || local > SHOOTING_STAR_FLIGHT) return null;
  var k = local / SHOOTING_STAR_FLIGHT;
  var s = Math.max(0.05, Number(o.scaleUi) || 1);
  var dir = hash(win * 9.1) < 0.5 ? -1 : 1;
  var angle = dir < 0 ? Math.PI - 0.42 - hash(win * 4.3) * 0.25 : 0.42 + hash(win * 4.3) * 0.25;
  var travel = Math.min(o.width * 0.45, 420 * s);
  var x0 = o.width * (0.18 + 0.64 * hash(win * 6.7));
  var y0 = o.skyH * (0.06 + 0.3 * hash(win * 8.9));
  var ease = 1 - Math.pow(1 - k, 2);
  // Grows in, streaks, then the tail catches up and the head fades.
  var tail = travel * 0.38 * Math.sin(Math.min(1, k * 1.25) * Math.PI * 0.5) * (1 - 0.5 * k);
  return {
    x: x0 + Math.cos(angle) * travel * ease,
    y: y0 + Math.sin(angle) * travel * ease,
    angle: angle,
    tail: tail,
    r: (4.5 + 2 * (1 - k)) * s,
    alpha: o.starAlpha * Math.min(1, (1 - k) * 2.2),
  };
}

/** Sparkle tone on the sea: white by day, pale gold at sunset, silver-blue under the moon. */
export function seaSparkleRgb(isMoon, sunset) {
  if (isMoon) return [226, 238, 255];
  var k = Math.max(0, Math.min(1, sunset || 0));
  return [255, Math.round(255 - 30 * k), Math.round(255 - 90 * k)];
}

/**
 * Canvas 2D: the sea sparkles (call after the glare).
 * @param {CanvasRenderingContext2D} ctx
 */
export function drawSeaSparkles(ctx, sparkles, rgb) {
  if (!ctx || !sparkles || !sparkles.length || typeof ctx.drawImage !== "function") return;
  var sprite = getStarCanvas("sparkle", rgb);
  if (!sprite) return;
  var prev = ctx.globalAlpha;
  for (var i = 0; i < sparkles.length; i++) {
    var sp = sparkles[i];
    var r = sp.r / STAR_SPRITE_FILL;
    ctx.globalAlpha = prev * sp.alpha;
    ctx.drawImage(sprite, sp.x - r, sp.y - r, r * 2, r * 2);
  }
  ctx.globalAlpha = prev;
}

/**
 * Canvas 2D: the shooting star — the baked tapered tail stretched behind a sparkle head.
 * @param {CanvasRenderingContext2D} ctx
 */
export function drawShootingStar(ctx, star) {
  if (!ctx || !star || typeof ctx.drawImage !== "function") return;
  var prev = ctx.globalAlpha;
  ctx.save();
  ctx.translate(star.x, star.y);
  ctx.globalAlpha = prev * star.alpha;
  var streak = getStreakCanvas();
  if (streak && star.tail > 1) {
    ctx.rotate(star.angle);
    var bandH = star.r * 1.6;
    var fullH = bandH / STREAK_BAND;
    ctx.drawImage(streak, -star.tail, -fullH / 2, star.tail, fullH);
    ctx.rotate(-star.angle);
  }
  var sprite = getStarCanvas("sparkle", SHOOTING_STAR_HEAD_RGB);
  if (sprite) {
    var r = star.r / STAR_SPRITE_FILL;
    ctx.drawImage(sprite, -r, -r, r * 2, r * 2);
  }
  ctx.restore();
  ctx.globalAlpha = prev;
}
